import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { StatsCard } from 'common/components';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import useDatatable, {
  addRingCentralContactToStore,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import API, { SECONDARY_API_CLIENT } from 'services/API';
import { SEARCH_PROJECT_CREATED_FROM_VARIANTS } from 'services/searchProjects';
import strings from 'strings';
import { FPIcon, When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DataTable from 'UI/components/organisms/DataTable';
import { SEARCH_PROJECT_SOURCES } from 'UI/constants/defaults';
import { ContactRole, EntityType, SearchProjectEntities } from 'UI/constants/entityTypes';
import { SvgInformationCircle, SvgMakeAi } from 'UI/res';
import { getErrorMessage } from 'UI/utils';

import FPQuickView from '../../../FPQuickView';
import { MatchMakingLoader } from '../MatchMakingLoader';

import {
  COPIES,
  MATCH_MODES,
  MatchedQueryParamMap,
  MILLISECONDS_INTERVAL,
  ORDER_BY_OPTIONS,
  RESPONSE_STATUSES
} from './MatchMakingTable.constants';
import {
  ActionsContainer,
  MatchMakingButtonsContainer,
  ToggleButtonStyled
} from './MatchMakingTable.styled';
import { getMatchEmptyStateLabels } from './MatchMakingTable.utils';

const getInitialMatchMode = ({ endpoint, secondaryEnpoint }) => {
  const hasBothEndpoints = Boolean(endpoint) && Boolean(secondaryEnpoint);
  if (hasBothEndpoints) return MATCH_MODES.default;
  if (secondaryEnpoint) return MATCH_MODES.aiMatch;
  return MATCH_MODES.default;
};

const getResponseError = rawResponse => {
  if (rawResponse?.error) return rawResponse.error;
  if (rawResponse?.data?.errorMessage || rawResponse?.data?.errorTitle) {
    return {
      message: rawResponse.data.errorMessage || null,
      title: rawResponse.data.errorTitle || null
    };
  }
  return null;
};

export const MatchMakingTable = ({
  columns: initialColumns,
  dataTableProps,
  disableTableActions,
  endpoint,
  entityTypeSelector,
  entityTypeToMatch,
  extraRenderers,
  formatResponse,
  isInteractionAllowed,
  onFetchingCompleted,
  onFetchingStarted,
  onProcessCompleted,
  onProcessStarted,
  profileId,
  secondaryEnpoint,
  tablePreferencesKey
}) => {
  const shouldShowMatchModeSelector = Boolean(endpoint) && Boolean(secondaryEnpoint);
  const dispatch = useDispatch();
  const { showAlert } = useShowAlert();
  const [matchMode, setMatchMode] = useState(() =>
    getInitialMatchMode({ endpoint, secondaryEnpoint })
  );
  const [isPolling, setIsPolling] = useState(false);
  const [isStartMatchLoading, setIsStartMatchLoading] = useState(false);
  const pollingRef = useRef(null);
  const isFetchingRef = useRef(null);
  const initialPreferences = getColumnPreferences(
    tablePreferencesKey,
    0,
    ORDER_BY_OPTIONS,
    initialColumns
  );
  const isDefaultMatch = matchMode === MATCH_MODES.default;
  const isAIMatch = matchMode === MATCH_MODES.aiMatch;

  const buildEndpoint = useCallback(() => {
    if (isAIMatch && secondaryEnpoint) return secondaryEnpoint.replace(':id', profileId);
    return endpoint && endpoint.replace(':id', profileId);
  }, [isAIMatch, secondaryEnpoint, endpoint, profileId]);

  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState,
    queryParams,
    rawResponse
  } = useDatatable({
    ...dataTableProps,
    apiInstance: isAIMatch && SECONDARY_API_CLIENT,
    columnsDefinitions: initialPreferences,
    endpoint: buildEndpoint(),
    formatResponse,
    initialPreferences,
    key: tablePreferencesKey,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    sendDateWithTimezone: true,
    shouldExcludeUserFilterParam: true,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage } = listState;
  const responseStatus = rawResponse?.data?.status;
  const responseStep = rawResponse?.data?.step;
  const responseError = getResponseError(rawResponse);
  const isMatchRunning = responseStatus === RESPONSE_STATUSES.running;
  const isMatchCompleted = responseStatus === RESPONSE_STATUSES.completed;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  useEffect(() => {
    if (showWholeSkeleton) {
      onFetchingStarted && onFetchingStarted();
    } else {
      onFetchingCompleted && onFetchingCompleted();
    }
  }, [onFetchingStarted, onFetchingCompleted, showWholeSkeleton]);

  const { entityId, quickViewState, listViewPreset, quickViewEntity, toggleQuickViewPreview } =
    useQuickViewPreview({ columns, data, entityType: entityTypeToMatch.type });

  const dataForSelection = useMemo(
    () =>
      entityTypeToMatch.id === ContactRole.Name
        ? data.map(item => ({
            id: item.id,
            type: item.role || SearchProjectEntities.Contact,
            type_id: item.role_id || item.roleId || ContactRole.Name,
            status: item.status
          }))
        : data.map(({ id }) => ({
            id,
            type_id: entityTypeToMatch.id,
            type: entityTypeToMatch.singular
          })),
    [data, entityTypeToMatch]
  );

  const {
    filteredItems,
    handleRowSelection,
    multiSelectComponents,
    selectedIndexesInPage,
    setToInitial: setSelectionToInitial,
    totalSelected
  } = useMultipleSelection({ data: dataForSelection, count, perPage });

  const matchedParam =
    entityTypeToMatch.matchedParamKey ?? MatchedQueryParamMap[entityTypeToMatch.singular];
  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    createdFrom: SEARCH_PROJECT_CREATED_FROM_VARIANTS.matchMaking,
    filteredItems,
    queryParams: {
      ...queryParams,
      params: `${queryParams?.params}&${matchedParam}Id=${profileId}&${matchedParam}Type=${
        isAIMatch ? 'ai' : 'default'
      }`,
      type: entityTypeToMatch.type
    },
    extraPayloadData: {
      ...(entityTypeToMatch.type === EntityType.Applicant
        ? { [`${matchedParam}Id`]: profileId }
        : {}),
      isFromMatchmaking: true,
      sourceId: SEARCH_PROJECT_SOURCES.matchMaking.id
    }
  });

  const handleAction = ({ rowData, origin }) => {
    addRingCentralContactToStore({
      rowData,
      columns: finalColumns,
      dispatch,
      role: entityTypeToMatch.type,
      origin
    });
  };

  const actionButtons = buildRowActionsRendererDefinition({
    actions: [listViewPreset],
    namespace: entityTypeToMatch.type,
    handleAction,
    columns: initialColumns
  });

  const finalColumns = getColumnsToRender(
    disableTableActions ? columns : [...columns, actionButtons],
    extraRenderers
  );

  const startPolling = useCallback(
    ({ shouldStartPolling = false } = {}) => {
      if (pollingRef.current || (!isPolling && !shouldStartPolling)) return;

      pollingRef.current = setInterval(async () => {
        try {
          if (isFetchingRef.current) return;
          isFetchingRef.current = true;
          await getData();
          setIsStartMatchLoading(false);

          if (
            responseStatus === RESPONSE_STATUSES.completed ||
            responseStatus === RESPONSE_STATUSES.failed
          ) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setIsPolling(false);
            onFetchingCompleted && onFetchingCompleted();
          }
          isFetchingRef.current = false;
        } catch (error) {
          isFetchingRef.current = false;
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsPolling(false);
          setIsStartMatchLoading(false);
          onFetchingCompleted && onFetchingCompleted();
          onProcessCompleted && onProcessCompleted();
        }
      }, MILLISECONDS_INTERVAL);
    },
    [getData, isPolling, responseStatus, onProcessCompleted, onFetchingCompleted]
  );

  const handleRefreshListClick = async () => {
    setIsStartMatchLoading(true);
    onProcessStarted && onProcessStarted();
    try {
      const finalEndpoint = buildEndpoint();
      const response = isDefaultMatch
        ? await API.post(finalEndpoint)
        : await SECONDARY_API_CLIENT.post(finalEndpoint);

      if ([HTTPStatusCodes.Ok, HTTPStatusCodes.NoContent].includes(response.status)) {
        setIsPolling(true);
        onFetchingStarted && onFetchingStarted();
        startPolling({ shouldStartPolling: true });
      } else {
        setIsStartMatchLoading(false);
        onProcessCompleted && onProcessCompleted();
      }
    } catch (error) {
      showAlert({
        body: getErrorMessage(error, COPIES.alerts.error.body),
        title: COPIES.alerts.error.title,
        severity: 'error'
      });
      setIsStartMatchLoading(false);
      onProcessCompleted && onProcessCompleted();
    }
  };

  useEffect(() => {
    if (isMatchRunning) {
      setIsPolling(true);
      startPolling();
      onFetchingStarted && onFetchingStarted();
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setIsPolling(false);
      onFetchingCompleted && onFetchingCompleted();
    };
  }, [startPolling, isMatchRunning, onFetchingStarted, onFetchingCompleted]);

  const buttonDefinitions = [
    {
      props: {
        disabled: !isInteractionAllowed,
        icon: isAIMatch && SvgMakeAi,
        onClick: handleRefreshListClick,
        text: isStartMatchLoading
          ? COPIES.actionButtons.onProgressText
          : strings.formatString(COPIES.actionButtons.matchEntityType, {
              entityType: entityTypeToMatch.title
            }),
        withGradient: !!isAIMatch,
        isSaving: isStartMatchLoading
      }
    }
  ];

  const toggleOptionsMatchType = [
    {
      title: COPIES.matchTypes.default,
      value: MATCH_MODES.default,
      disabled: !isInteractionAllowed
    },
    {
      title: COPIES.matchTypes.ai,
      value: MATCH_MODES.aiMatch,
      disabled: !isInteractionAllowed
    }
  ];

  const onToggleMatchType = value => {
    handlePageChange(0);
    setMatchMode(value);
    setSelectionToInitial();
  };

  const handleTabRefresh = async () => {
    await getData();
  };

  const matchEmptyStateLabels = useMemo(
    () =>
      getMatchEmptyStateLabels({
        entityType: entityTypeToMatch.title,
        responseError,
        responseStatus
      }),
    [entityTypeToMatch.title, responseError, responseStatus]
  );

  const footer = (
    <Box p={2} borderRadius={1} display="flex">
      <FPIcon
        role="img"
        aria-label="Info"
        component={SvgInformationCircle}
        onSetColor={palette => palette.info.main}
        size={18}
      />
      &nbsp;&nbsp;
      <Typography variant="body2">
        {isAIMatch ? COPIES.labels.tableFooterAi : COPIES.labels.tableFooterBasic}
      </Typography>
    </Box>
  );

  return (
    <>
      <ActionsContainer container>
        <Grid item xs={12} sm={4}>
          {entityTypeSelector}
        </Grid>

        <MatchMakingButtonsContainer item xs={12} sm={8}>
          <FPButtonBuilder
            GridContainerProps={{ justifyContent: 'flex-end', spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />

          {shouldShowMatchModeSelector && (
            <ToggleButtonStyled
              onChange={onToggleMatchType}
              options={toggleOptionsMatchType}
              size="large"
            />
          )}
        </MatchMakingButtonsContainer>
      </ActionsContainer>

      {isMatchRunning ? (
        <MatchMakingLoader status={responseStatus} step={responseStep} />
      ) : (
        <StatsCard footer={footer} key={entityTypeToMatch?.id} paddingContent={0}>
          <DataTable
            elevation={0}
            columnOrder={columnOrder?.length ? columnOrder : undefined}
            columns={finalColumns}
            components={multiSelectComponents}
            count={count}
            CustomToolbar={
              <CustomTableToolbarSelect total={totalSelected}>
                <SearchProjectAction />
              </CustomTableToolbarSelect>
            }
            data={isMatchCompleted ? data : []}
            draggableColumns={{
              enabled: true
            }}
            filter={false}
            loading={showWholeSkeleton}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnSortChange={handleColumnSortChange}
            onPageClick={handlePageChange}
            onPerPageClick={handlePerPageChange}
            onRowSelectionChange={handleRowSelection}
            onSearchTextChange={handleKeywordChange}
            onToggleFilters={handleFiltersToggle}
            page={page}
            pagination={isEmpty(isMatchCompleted ? data : []) && false}
            refreshing={isLoading}
            rowsPerPage={perPage}
            rowsSelected={selectedIndexesInPage}
            search={false}
            selectToolbarPlacement="none"
            sortOrder={{ name: orderBy, direction }}
            {...matchEmptyStateLabels}
            {...(isEmpty(isMatchCompleted ? data : []) && { customFooter: null })}
          />
        </StatsCard>
      )}

      <SearchProjectForms />

      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{
            open: quickViewState.isQuickViewOpen
          }}
          entityType={quickViewEntity}
          onClose={toggleQuickViewPreview}
          itemsToNavigate={data}
          id={entityId}
          onEditionCompleted={handleTabRefresh}
        />
      </When>
    </>
  );
};
